/**
 * CL-PRODUCT-SELECTIONS-006A / 006A-R2 — Commercial, cost calculator, and delivery fields (pure).
 *
 * Staff-side cost fields remain internal. Client projections use explicit allowlists.
 * Monetary calculations use integer cents internally; persisted amounts are decimal numbers
 * rounded to currency scale (2 dp) at boundaries.
 */

function safeStr(value) {
  return String(value ?? "").trim();
}

export const PRODUCT_COST_METHOD = Object.freeze({
  QUANTITY: "calculate_from_quantity",
  QUOTED_TOTAL: "use_supplier_quoted_total",
});

export const PRODUCT_COST_METHOD_LABELS = Object.freeze({
  [PRODUCT_COST_METHOD.QUANTITY]: "Calculate from quantity",
  [PRODUCT_COST_METHOD.QUOTED_TOTAL]: "Use supplier quoted total",
});

export const PRODUCT_COST_BASIS = Object.freeze({
  EACH: "each",
  SQUARE_FOOT: "square_foot",
  LINEAR_FOOT: "linear_foot",
  BOX_PACKAGE: "box_package",
  LUMP_SUM: "lump_sum",
  ALLOWANCE: "allowance",
  TOTAL: "total",
  OTHER: "other",
});

export const PRODUCT_DELIVERY_STATUS = Object.freeze({
  NOT_ORDERED: "not_ordered",
  QUOTED: "quoted",
  ORDERED: "ordered",
  IN_PRODUCTION: "in_production",
  SHIPPED: "shipped",
  DELIVERED: "delivered",
  DELAYED: "delayed",
  CANCELLED: "cancelled",
});

export const PRODUCT_SCHEDULE_RISK = Object.freeze({
  NONE: "none",
  LOW: "low",
  MEDIUM: "medium",
  HIGH: "high",
});

export const PRODUCT_COST_BASIS_LABELS = Object.freeze({
  [PRODUCT_COST_BASIS.EACH]: "Each",
  [PRODUCT_COST_BASIS.SQUARE_FOOT]: "Square foot",
  [PRODUCT_COST_BASIS.LINEAR_FOOT]: "Linear foot",
  [PRODUCT_COST_BASIS.BOX_PACKAGE]: "Box/package",
  [PRODUCT_COST_BASIS.LUMP_SUM]: "Lump sum",
  [PRODUCT_COST_BASIS.ALLOWANCE]: "Allowance",
  [PRODUCT_COST_BASIS.TOTAL]: "Total",
  [PRODUCT_COST_BASIS.OTHER]: "Other",
});

export const PRODUCT_DELIVERY_STATUS_LABELS = Object.freeze({
  [PRODUCT_DELIVERY_STATUS.NOT_ORDERED]: "Not ordered",
  [PRODUCT_DELIVERY_STATUS.QUOTED]: "Quoted",
  [PRODUCT_DELIVERY_STATUS.ORDERED]: "Ordered",
  [PRODUCT_DELIVERY_STATUS.IN_PRODUCTION]: "In production",
  [PRODUCT_DELIVERY_STATUS.SHIPPED]: "Shipped",
  [PRODUCT_DELIVERY_STATUS.DELIVERED]: "Delivered",
  [PRODUCT_DELIVERY_STATUS.DELAYED]: "Delayed",
  [PRODUCT_DELIVERY_STATUS.CANCELLED]: "Cancelled",
});

export const PRODUCT_SCHEDULE_RISK_LABELS = Object.freeze({
  [PRODUCT_SCHEDULE_RISK.NONE]: "On track",
  [PRODUCT_SCHEDULE_RISK.LOW]: "Low risk",
  [PRODUCT_SCHEDULE_RISK.MEDIUM]: "Medium risk",
  [PRODUCT_SCHEDULE_RISK.HIGH]: "High risk",
});

/** Staff canonical option + publication: decimal amounts; `clientPriceCents` is integer cents (line total preferred). */
export const PRODUCT_OPTION_CLIENT_FACING_PRICE_FIELD_KEYS = Object.freeze([
  "clientUnitPrice",
  "clientLineTotal",
  "clientPriceCents",
  "clientPriceCurrency",
]);

export const PRODUCT_OPTION_COMMERCIAL_STAFF_FIELDS = Object.freeze([
  "costMethod",
  "supplierQuote",
  "unitCost",
  "tax",
  "taxRate",
  "taxIncluded",
  "currency",
  "costBasis",
  "quantity",
  "wastePercent",
  "deliveryFreight",
  "otherProductCost",
  "otherProductCostDescription",
  "quoteCheckedAt",
  "quoteReferenceNumber",
  "quotedTotalIncludesDelivery",
  "quotedTotalIncludesTax",
  "estimatedLeadTimeDays",
  "orderByAt",
  "expectedDeliveryAt",
  "requiredOnSiteAt",
  "actualDeliveryAt",
  "deliveryStatus",
  "scheduleRisk",
  "deliveryEstimateSource",
  "estimatedProductTotal",
  "estimatedProductTotalBasisLabel",
  "linkedBudgetItemId",
  "budgetForecastAppliedAt",
  "budgetForecastSourceOptionId",
  "internalNotes",
]);

export const PRODUCT_OPTION_COMMERCIAL_DATE_FIELDS = Object.freeze([
  "quoteCheckedAt",
  "orderByAt",
  "expectedDeliveryAt",
  "requiredOnSiteAt",
  "actualDeliveryAt",
]);

/** Fields that may appear in sanitized client projections when explicitly published. */
export const PRODUCT_OPTION_CLIENT_PUBLISHABLE_COMMERCIAL_FIELDS = Object.freeze([
  "publishedSupplierName",
  "publishedSupplierOrganizationId",
  "publishedDecisionStatusLabel",
  "publishedExpectedDeliveryAt",
  "publishedLeadTimeLabel",
  "publishedDeliveryStatusLabel",
  "publishedScheduleRiskLabel",
]);

const COST_BASIS_VALUES = new Set(Object.values(PRODUCT_COST_BASIS));
const COST_METHOD_VALUES = new Set(Object.values(PRODUCT_COST_METHOD));
const DELIVERY_STATUS_VALUES = new Set(Object.values(PRODUCT_DELIVERY_STATUS));
const SCHEDULE_RISK_VALUES = new Set(Object.values(PRODUCT_SCHEDULE_RISK));
const CURRENCY_SCALE = 2;

export function isValidProductCostBasis(value) {
  const key = safeStr(value);
  return !key || COST_BASIS_VALUES.has(key);
}

export function isValidProductCostMethod(value) {
  const key = safeStr(value);
  return !key || COST_METHOD_VALUES.has(key);
}

export function isValidProductDeliveryStatus(value) {
  const key = safeStr(value);
  return !key || DELIVERY_STATUS_VALUES.has(key);
}

export function isValidProductScheduleRisk(value) {
  const key = safeStr(value);
  return !key || SCHEDULE_RISK_VALUES.has(key);
}

export function moneyToCents(value) {
  if (value == null || value === "") return null;
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  return Math.round(n * 10 ** CURRENCY_SCALE);
}

export function centsToMoney(cents) {
  if (cents == null || cents === "") return null;
  const n = Number(cents);
  if (!Number.isFinite(n)) return null;
  return Number((n / 10 ** CURRENCY_SCALE).toFixed(CURRENCY_SCALE));
}

export function parseProductMoneyInput(value, { allowEmpty = true } = {}) {
  if (value == null || value === "") {
    return allowEmpty
      ? { ok: true, amount: null, cents: null, isEmpty: true }
      : { ok: false, error: "Enter an amount." };
  }
  const normalized = safeStr(value).replace(/[$,\s]/g, "");
  if (!normalized) {
    return allowEmpty
      ? { ok: true, amount: null, cents: null, isEmpty: true }
      : { ok: false, error: "Enter an amount." };
  }
  const parsed = Number(normalized);
  if (!Number.isFinite(parsed)) return { ok: false, error: "Enter a valid amount." };
  if (parsed < 0) return { ok: false, error: "Amount cannot be negative." };
  const cents = Math.round(parsed * 10 ** CURRENCY_SCALE);
  return { ok: true, amount: centsToMoney(cents), cents, isEmpty: false };
}

export function parseProductQuantityInput(value, { allowEmpty = true } = {}) {
  if (value == null || value === "") {
    return allowEmpty
      ? { ok: true, quantity: null, isEmpty: true }
      : { ok: false, error: "Enter a quantity." };
  }
  const normalized = safeStr(value).replace(/,/g, "");
  const parsed = Number(normalized);
  if (!Number.isFinite(parsed)) return { ok: false, error: "Enter a valid quantity." };
  if (parsed < 0) return { ok: false, error: "Quantity cannot be negative." };
  return { ok: true, quantity: parsed, isEmpty: false };
}

export function parseProductPercentInput(value, { allowEmpty = true, label = "Percentage" } = {}) {
  if (value == null || value === "") {
    return allowEmpty
      ? { ok: true, percent: null, isEmpty: true }
      : { ok: false, error: `Enter ${label.toLowerCase()}.` };
  }
  const normalized = safeStr(value).replace(/%/g, "");
  const parsed = Number(normalized);
  if (!Number.isFinite(parsed)) return { ok: false, error: `Enter a valid ${label.toLowerCase()}.` };
  if (parsed < 0) return { ok: false, error: `${label} cannot be negative.` };
  return { ok: true, percent: parsed, isEmpty: false };
}

export function formatDateOnlyFromDate(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return null;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export function formatCompactProductDisplayDate(value) {
  const normalized = normalizeProductDateOnly(value);
  if (!normalized.ok || normalized.isEmpty || !normalized.date) return "";
  const [year, month, day] = normalized.date.split("-").map(Number);
  const probe = new Date(year, month - 1, day);
  if (
    probe.getFullYear() !== year
    || probe.getMonth() !== month - 1
    || probe.getDate() !== day
  ) {
    return "";
  }
  return probe.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

export function normalizeProductDateOnly(value) {
  if (value == null || value === "") return { ok: true, date: null, isEmpty: true };
  if (typeof value === "object" && value?.toDate) {
    const date = formatDateOnlyFromDate(value.toDate());
    return date ? { ok: true, date, isEmpty: false } : { ok: false, error: "Enter a valid date." };
  }
  const text = safeStr(value);
  if (/^\d{4}-\d{2}-\d{2}$/.test(text)) {
    const [year, month, day] = text.split("-").map(Number);
    const probe = new Date(year, month - 1, day);
    if (
      probe.getFullYear() !== year
      || probe.getMonth() !== month - 1
      || probe.getDate() !== day
    ) {
      return { ok: false, error: "Enter a valid date." };
    }
    return { ok: true, date: text, isEmpty: false };
  }
  return { ok: false, error: "Use YYYY-MM-DD." };
}

export function formatMoneyDisplay(amount, currency = "CAD") {
  if (amount == null || amount === "") return "";
  const n = Number(amount);
  if (!Number.isFinite(n)) return "";
  const code = safeStr(currency).toUpperCase() || "CAD";
  return new Intl.NumberFormat("en-CA", {
    style: "currency",
    currency: code,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);
}

export function computeProductCostBreakdown(input = {}, { strict = false } = {}) {
  const costMethod = safeStr(input.costMethod) || PRODUCT_COST_METHOD.QUANTITY;
  const currency = safeStr(input.currency).toUpperCase() || "CAD";
  const taxIncluded = input.taxIncluded === true;
  const taxRateResult = parseProductPercentInput(input.taxRate, { label: "Tax rate" });
  if (!taxRateResult.ok) return taxRateResult;
  const taxRate = taxRateResult.percent ?? 0;

  const freightResult = parseProductMoneyInput(input.deliveryFreight);
  if (!freightResult.ok) return freightResult;
  const otherResult = parseProductMoneyInput(input.otherProductCost);
  if (!otherResult.ok) return otherResult;

  const freightCents = freightResult.cents ?? 0;
  const otherCents = otherResult.cents ?? 0;

  if (costMethod === PRODUCT_COST_METHOD.QUOTED_TOTAL) {
    const quoteResult = parseProductMoneyInput(input.supplierQuote, { allowEmpty: !strict });
    if (!quoteResult.ok) return quoteResult;
    if (quoteResult.isEmpty && !strict) {
      return {
        ok: true,
        costMethod,
        currency,
        baseQuantity: null,
        wasteQuantity: null,
        billableQuantity: null,
        materialsSubtotalCents: 0,
        deliveryFreightCents: freightCents,
        otherCostCents: otherCents,
        preTaxTotalCents: freightCents + otherCents,
        taxCents: 0,
        estimatedTotalCents: freightCents + otherCents,
        taxIncluded,
        taxRate,
      };
    }
    if (quoteResult.isEmpty) return { ok: false, error: "Enter the supplier quoted total." };
    const quotedCents = quoteResult.cents ?? 0;
    const includesDelivery = input.quotedTotalIncludesDelivery === true;
    const includesTax = input.quotedTotalIncludesTax === true;
    const deliveryCents = includesDelivery ? 0 : freightCents;
    const preTaxCents = quotedCents + deliveryCents + otherCents;
    let taxCents = 0;
    let totalCents = preTaxCents;
    if (includesTax) {
      taxCents = Math.round(preTaxCents - (preTaxCents / (1 + taxRate / 100)));
      totalCents = preTaxCents;
    } else if (taxRate > 0) {
      taxCents = Math.round(preTaxCents * (taxRate / 100));
      totalCents = preTaxCents + taxCents;
    }
    return {
      ok: true,
      costMethod,
      currency,
      baseQuantity: null,
      wasteQuantity: null,
      billableQuantity: null,
      materialsSubtotalCents: quotedCents,
      deliveryFreightCents: deliveryCents,
      otherCostCents: otherCents,
      preTaxTotalCents: preTaxCents,
      taxCents,
      estimatedTotalCents: totalCents,
      taxIncluded,
      taxRate,
    };
  }

  const quantityResult = parseProductQuantityInput(input.quantity, { allowEmpty: !strict });
  if (!quantityResult.ok) return quantityResult;
  const unitCostResult = parseProductMoneyInput(input.unitCost, { allowEmpty: !strict });
  if (!unitCostResult.ok) return unitCostResult;
  const wasteResult = parseProductPercentInput(input.wastePercent, { label: "Waste percentage" });
  if (!wasteResult.ok) return wasteResult;

  if ((quantityResult.isEmpty || unitCostResult.isEmpty) && !strict) {
    const preTaxCents = freightCents + otherCents;
    return {
      ok: true,
      costMethod,
      currency,
      baseQuantity: quantityResult.quantity,
      wasteQuantity: null,
      billableQuantity: null,
      materialsSubtotalCents: 0,
      deliveryFreightCents: freightCents,
      otherCostCents: otherCents,
      preTaxTotalCents: preTaxCents,
      taxCents: 0,
      estimatedTotalCents: preTaxCents,
      taxIncluded,
      taxRate,
    };
  }
  if (quantityResult.isEmpty) return { ok: false, error: "Enter a quantity." };
  if (unitCostResult.isEmpty) return { ok: false, error: "Enter a unit cost." };

  const baseQuantity = quantityResult.quantity ?? 0;
  const wastePercent = wasteResult.percent ?? 0;
  const billableQuantity = Number((baseQuantity * (1 + wastePercent / 100)).toFixed(4));
  const unitCostCents = unitCostResult.cents ?? 0;
  const materialsSubtotalCents = Math.round(billableQuantity * unitCostCents);
  const preTaxCents = materialsSubtotalCents + freightCents + otherCents;
  let taxCents = 0;
  let totalCents = preTaxCents;
  if (taxIncluded && taxRate > 0) {
    taxCents = Math.round(preTaxCents - (preTaxCents / (1 + taxRate / 100)));
    totalCents = preTaxCents;
  } else if (taxRate > 0) {
    taxCents = Math.round(preTaxCents * (taxRate / 100));
    totalCents = preTaxCents + taxCents;
  }

  return {
    ok: true,
    costMethod,
    currency,
    baseQuantity,
    wasteQuantity: Number((billableQuantity - baseQuantity).toFixed(4)),
    billableQuantity,
    materialsSubtotalCents,
    deliveryFreightCents: freightCents,
    otherCostCents: otherCents,
    preTaxTotalCents: preTaxCents,
    taxCents,
    estimatedTotalCents: totalCents,
    taxIncluded,
    taxRate,
  };
}

export function buildEstimatedProductTotalBasisLabel(option = {}, breakdown = null) {
  const basis = PRODUCT_COST_BASIS_LABELS[safeStr(option.costBasis)] || "";
  const method = safeStr(option.costMethod) || PRODUCT_COST_METHOD.QUANTITY;
  const currency = safeStr(option.currency).toUpperCase() || "CAD";
  const total = breakdown?.estimatedTotalCents != null
    ? centsToMoney(breakdown.estimatedTotalCents)
    : option.estimatedProductTotal;
  if (total == null) return "";
  const formatted = formatMoneyDisplay(total, currency);
  if (method === PRODUCT_COST_METHOD.QUOTED_TOTAL) {
    return `${formatted} ${currency} · supplier quoted total`;
  }
  const billable = breakdown?.billableQuantity ?? option.quantity;
  const waste = breakdown?.wasteQuantity != null
    ? option.wastePercent
    : option.wastePercent;
  if (billable != null && basis) {
    const wasteLabel = waste ? ` including ${waste}% waste` : "";
    return `${formatted} ${currency} · ${billable} ${basis.toLowerCase()}${wasteLabel}`;
  }
  return `${formatted} ${currency}${basis ? ` · ${basis}` : ""}`;
}

export function computeSuggestedOrderByDate({
  requiredOnSiteAt = "",
  expectedDeliveryAt = "",
  estimatedLeadTimeDays = null,
} = {}) {
  const required = normalizeProductDateOnly(requiredOnSiteAt);
  const expected = normalizeProductDateOnly(expectedDeliveryAt);
  const targetDate = required.ok && required.date
    ? required.date
    : (expected.ok && expected.date ? expected.date : null);
  if (!targetDate) return null;
  const leadDays = Number(estimatedLeadTimeDays);
  if (!Number.isFinite(leadDays) || leadDays <= 0) return null;
  const [year, month, day] = targetDate.split("-").map(Number);
  const target = new Date(year, month - 1, day);
  target.setDate(target.getDate() - Math.ceil(leadDays));
  return formatDateOnlyFromDate(target);
}

export function buildProductScheduleWarnings(input = {}) {
  const warnings = [];
  const orderBy = normalizeProductDateOnly(input.orderByAt);
  const expected = normalizeProductDateOnly(input.expectedDeliveryAt);
  const required = normalizeProductDateOnly(input.requiredOnSiteAt);
  if (orderBy.ok && expected.ok && orderBy.date && expected.date && expected.date < orderBy.date) {
    warnings.push("Expected delivery is before the order-by date.");
  }
  if (required.ok && expected.ok && required.date && expected.date && required.date < expected.date) {
    warnings.push("Required on-site date is earlier than expected delivery.");
  }
  const leadDays = Number(input.estimatedLeadTimeDays);
  const suggested = computeSuggestedOrderByDate(input);
  if (
    Number.isFinite(leadDays)
    && leadDays > 0
    && orderBy.ok
    && orderBy.date
    && suggested
    && orderBy.date > suggested
  ) {
    warnings.push(`Order-by date is later than the ${leadDays}-day lead-time suggestion (${suggested}).`);
  }
  return warnings;
}

function normalizeStoredNumber(value) {
  if (value == null || value === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function normalizeBooleanField(value, fallback = false) {
  if (value === true || value === false) return value;
  return fallback;
}

export function normalizeProductCommercialFields(input = {}) {
  const costMethod = safeStr(input.costMethod) || PRODUCT_COST_METHOD.QUANTITY;
  const costBasis = safeStr(input.costBasis) || null;
  const deliveryStatus = safeStr(input.deliveryStatus) || null;
  const scheduleRisk = safeStr(input.scheduleRisk) || PRODUCT_SCHEDULE_RISK.NONE;

  if (!isValidProductCostMethod(costMethod)) {
    return { ok: false, error: "Choose a valid cost method." };
  }
  if (!isValidProductCostBasis(costBasis)) {
    return { ok: false, error: "Choose a valid unit of measure." };
  }
  if (!isValidProductDeliveryStatus(deliveryStatus)) {
    return { ok: false, error: "Choose a valid delivery status." };
  }
  if (!isValidProductScheduleRisk(scheduleRisk)) {
    return { ok: false, error: "Choose a valid schedule risk indicator." };
  }

  const currencyRaw = safeStr(input.currency).toUpperCase();
  const currency = currencyRaw || "CAD";

  const supplierQuote = parseProductMoneyInput(input.supplierQuote);
  if (!supplierQuote.ok) return supplierQuote;
  const unitCost = parseProductMoneyInput(input.unitCost);
  if (!unitCost.ok) return unitCost;
  const deliveryFreight = parseProductMoneyInput(input.deliveryFreight);
  if (!deliveryFreight.ok) return deliveryFreight;
  const otherProductCost = parseProductMoneyInput(input.otherProductCost);
  if (!otherProductCost.ok) return otherProductCost;
  const quantity = parseProductQuantityInput(input.quantity);
  if (!quantity.ok) return quantity;
  const wastePercent = parseProductPercentInput(input.wastePercent, { label: "Waste percentage" });
  if (!wastePercent.ok) return wastePercent;
  const taxRate = parseProductPercentInput(input.taxRate, { label: "Tax rate" });
  if (!taxRate.ok) return taxRate;
  const estimatedLeadTimeDays = parseProductQuantityInput(input.estimatedLeadTimeDays, { allowEmpty: true });
  if (!estimatedLeadTimeDays.ok) return estimatedLeadTimeDays;

  const quoteCheckedAt = normalizeProductDateOnly(input.quoteCheckedAt);
  if (!quoteCheckedAt.ok) return quoteCheckedAt;
  const orderByAt = normalizeProductDateOnly(input.orderByAt);
  if (!orderByAt.ok) return orderByAt;
  const expectedDeliveryAt = normalizeProductDateOnly(input.expectedDeliveryAt);
  if (!expectedDeliveryAt.ok) return expectedDeliveryAt;
  const requiredOnSiteAt = normalizeProductDateOnly(input.requiredOnSiteAt);
  if (!requiredOnSiteAt.ok) return requiredOnSiteAt;
  const actualDeliveryAt = normalizeProductDateOnly(input.actualDeliveryAt);
  if (!actualDeliveryAt.ok) return actualDeliveryAt;

  const breakdown = computeProductCostBreakdown({
    costMethod,
    currency,
    supplierQuote: supplierQuote.amount,
    unitCost: unitCost.amount,
    quantity: quantity.quantity,
    wastePercent: wastePercent.percent,
    deliveryFreight: deliveryFreight.amount,
    otherProductCost: otherProductCost.amount,
    taxRate: taxRate.percent,
    taxIncluded: normalizeBooleanField(input.taxIncluded, false),
    quotedTotalIncludesDelivery: normalizeBooleanField(input.quotedTotalIncludesDelivery, false),
    quotedTotalIncludesTax: normalizeBooleanField(input.quotedTotalIncludesTax, false),
  }, { strict: false });
  if (!breakdown.ok) return breakdown;

  const legacyTax = normalizeStoredNumber(input.tax);

  return {
    ok: true,
    costMethod,
    supplierQuote: supplierQuote.amount,
    unitCost: unitCost.amount,
    tax: legacyTax,
    taxRate: taxRate.percent,
    taxIncluded: normalizeBooleanField(input.taxIncluded, false),
    currency,
    costBasis,
    quantity: quantity.quantity,
    wastePercent: wastePercent.percent,
    deliveryFreight: deliveryFreight.amount,
    otherProductCost: otherProductCost.amount,
    otherProductCostDescription: safeStr(input.otherProductCostDescription) || null,
    quoteCheckedAt: quoteCheckedAt.date,
    quoteReferenceNumber: safeStr(input.quoteReferenceNumber) || null,
    quotedTotalIncludesDelivery: normalizeBooleanField(input.quotedTotalIncludesDelivery, false),
    quotedTotalIncludesTax: normalizeBooleanField(input.quotedTotalIncludesTax, false),
    estimatedLeadTimeDays: estimatedLeadTimeDays.quantity,
    orderByAt: orderByAt.date,
    expectedDeliveryAt: expectedDeliveryAt.date,
    requiredOnSiteAt: requiredOnSiteAt.date,
    actualDeliveryAt: actualDeliveryAt.date,
    deliveryStatus,
    scheduleRisk,
    deliveryEstimateSource: safeStr(input.deliveryEstimateSource) || null,
    estimatedProductTotal: breakdown.estimatedTotalCents > 0 || supplierQuote.amount != null || unitCost.amount != null
      ? centsToMoney(breakdown.estimatedTotalCents)
      : null,
    estimatedProductTotalBasisLabel: buildEstimatedProductTotalBasisLabel({
      costMethod,
      costBasis,
      currency,
      quantity: quantity.quantity,
      wastePercent: wastePercent.percent,
    }, breakdown),
    linkedBudgetItemId: input.linkedBudgetItemId === null
      ? null
      : (safeStr(input.linkedBudgetItemId) || undefined),
    budgetForecastAppliedAt: input.budgetForecastAppliedAt === null
      ? null
      : (safeStr(input.budgetForecastAppliedAt) || undefined),
    budgetForecastSourceOptionId: input.budgetForecastSourceOptionId === null
      ? null
      : (safeStr(input.budgetForecastSourceOptionId) || undefined),
    costBreakdown: breakdown,
  };
}

export function resolveCanonicalClientPriceCents({ clientLineTotal = null, clientUnitPrice = null, clientPriceCents = null } = {}) {
  const line = parseProductMoneyInput(clientLineTotal);
  if (!line.ok) return line;
  const unit = parseProductMoneyInput(clientUnitPrice);
  if (!unit.ok) return unit;
  if (!line.isEmpty && line.cents != null) return { ok: true, cents: line.cents };
  if (!unit.isEmpty && unit.cents != null) return { ok: true, cents: unit.cents };
  if (clientPriceCents != null && clientPriceCents !== "") {
    const parsed = Number(clientPriceCents);
    if (Number.isFinite(parsed)) return { ok: true, cents: Math.round(parsed) };
  }
  return { ok: true, cents: null, isEmpty: true };
}

export function normalizeProductClientFacingPriceFields(input = {}, { currencyFallback = "CAD" } = {}) {
  const unit = parseProductMoneyInput(input.clientUnitPrice);
  if (!unit.ok) return unit;
  const line = parseProductMoneyInput(input.clientLineTotal);
  if (!line.ok) return line;
  const currency = safeStr(input.currency || input.clientPriceCurrency).toUpperCase() || currencyFallback;
  const centsResult = resolveCanonicalClientPriceCents({
    clientLineTotal: line.isEmpty ? null : line.amount,
    clientUnitPrice: unit.isEmpty ? null : unit.amount,
    clientPriceCents: input.clientPriceCents,
  });
  if (!centsResult.ok) return centsResult;
  const hasAny = !unit.isEmpty || !line.isEmpty || centsResult.cents != null;
  return {
    ok: true,
    clientUnitPrice: unit.isEmpty ? null : unit.amount,
    clientLineTotal: line.isEmpty ? null : line.amount,
    clientPriceCents: hasAny ? centsResult.cents : null,
    clientPriceCurrency: hasAny && centsResult.cents != null ? currency : null,
  };
}

export function verifyProductClientFacingPricePersistence(submitted = {}, persisted = {}) {
  const expected = normalizeProductClientFacingPriceFields(submitted);
  if (!expected.ok) return expected;
  const actual = normalizeProductClientFacingPriceFields(persisted);
  if (!actual.ok) return actual;
  const mismatch = (a, b) => {
    if (a == null && b == null) return false;
    if (a == null || b == null) return true;
    return Number(a) !== Number(b);
  };
  if (mismatch(expected.clientUnitPrice, actual.clientUnitPrice)
    || mismatch(expected.clientLineTotal, actual.clientLineTotal)
    || mismatch(expected.clientPriceCents, actual.clientPriceCents)) {
    return {
      ok: false,
      error: "Saved client-facing prices did not match what was submitted. Try saving again.",
    };
  }
  return { ok: true };
}

export function hydrateProductCommercialFormFields(option = {}) {
  const source = option && typeof option === "object" ? option : {};
  const formatStoredMoney = (value) => {
    if (value == null || value === "") return "";
    const n = Number(value);
    return Number.isFinite(n) ? String(n) : "";
  };
  const formatStoredNumber = (value) => {
    if (value == null || value === "") return "";
    const n = Number(value);
    return Number.isFinite(n) ? String(n) : "";
  };
  const formatStoredDate = (value) => {
    const normalized = normalizeProductDateOnly(value);
    return normalized.ok && normalized.date ? normalized.date : "";
  };

  return {
    costMethod: safeStr(source.costMethod) || PRODUCT_COST_METHOD.QUANTITY,
    supplierQuote: formatStoredMoney(source.supplierQuote),
    unitCost: formatStoredMoney(source.unitCost),
    quantity: formatStoredNumber(source.quantity),
    currency: safeStr(source.currency).toUpperCase() || "CAD",
    costBasis: safeStr(source.costBasis),
    wastePercent: formatStoredNumber(source.wastePercent),
    deliveryFreight: formatStoredMoney(source.deliveryFreight),
    otherProductCost: formatStoredMoney(source.otherProductCost),
    otherProductCostDescription: safeStr(source.otherProductCostDescription),
    taxRate: formatStoredNumber(source.taxRate ?? source.tax),
    taxIncluded: source.taxIncluded === true,
    quoteCheckedAt: formatStoredDate(source.quoteCheckedAt),
    quoteReferenceNumber: safeStr(source.quoteReferenceNumber),
    quotedTotalIncludesDelivery: source.quotedTotalIncludesDelivery === true,
    quotedTotalIncludesTax: source.quotedTotalIncludesTax === true,
    estimatedLeadTimeDays: formatStoredNumber(source.estimatedLeadTimeDays),
    orderByAt: formatStoredDate(source.orderByAt),
    expectedDeliveryAt: formatStoredDate(source.expectedDeliveryAt),
    requiredOnSiteAt: formatStoredDate(source.requiredOnSiteAt),
    actualDeliveryAt: formatStoredDate(source.actualDeliveryAt),
    deliveryStatus: safeStr(source.deliveryStatus),
    scheduleRisk: safeStr(source.scheduleRisk) || PRODUCT_SCHEDULE_RISK.NONE,
    deliveryEstimateSource: safeStr(source.deliveryEstimateSource),
    linkedBudgetItemId: safeStr(source.linkedBudgetItemId),
    budgetForecastAppliedAt: safeStr(source.budgetForecastAppliedAt),
    budgetForecastSourceOptionId: safeStr(source.budgetForecastSourceOptionId),
    clientUnitPrice: formatStoredMoney(source.clientUnitPrice),
    clientLineTotal: formatStoredMoney(source.clientLineTotal),
  };
}

export const PRODUCT_OPTION_COMMERCIAL_PERSISTED_FIELD_KEYS = Object.freeze(
  PRODUCT_OPTION_COMMERCIAL_STAFF_FIELDS.filter((key) => key !== "internalNotes")
);

export function buildProductCommercialSavePayload(form = {}) {
  const normalized = normalizeProductCommercialFields(form);
  if (!normalized.ok) return normalized;
  const clientPrices = normalizeProductClientFacingPriceFields({
    ...form,
    currency: normalized.currency || form.currency,
  });
  if (!clientPrices.ok) return clientPrices;
  const {
    costBreakdown: _breakdown,
    ok: _ok,
    ...persisted
  } = normalized;
  const {
    ok: _clientOk,
    ...clientFields
  } = clientPrices;
  return {
    ok: true,
    fields: {
      ...persisted,
      ...clientFields,
      _commercialFieldsIncluded: true,
    },
  };
}

export function formatProductCostSummary(option = {}) {
  if (safeStr(option.estimatedProductTotalBasisLabel)) {
    return safeStr(option.estimatedProductTotalBasisLabel);
  }
  const amount = normalizeStoredNumber(option.estimatedProductTotal)
    ?? normalizeStoredNumber(option.unitCost)
    ?? normalizeStoredNumber(option.supplierQuote);
  const currency = safeStr(option.currency).toUpperCase() || "CAD";
  const basis = PRODUCT_COST_BASIS_LABELS[safeStr(option.costBasis)] || "";
  const quantity = normalizeStoredNumber(option.quantity);
  if (amount == null) return "";
  const formatted = formatMoneyDisplay(amount, currency);
  if (quantity != null && quantity !== 1 && basis) {
    return `${formatted} · ${quantity} ${basis.toLowerCase()}`;
  }
  return basis ? `${formatted} · ${basis}` : formatted;
}

export function formatProductDeliverySummary(option = {}) {
  const status = PRODUCT_DELIVERY_STATUS_LABELS[safeStr(option.deliveryStatus)];
  const leadDays = normalizeStoredNumber(option.estimatedLeadTimeDays);
  if (safeStr(option.expectedDeliveryAt)) {
    return status
      ? `${status} · Expected ${safeStr(option.expectedDeliveryAt)}`
      : `Expected ${safeStr(option.expectedDeliveryAt)}`;
  }
  if (leadDays != null) return status ? `${status} · ${leadDays} day lead` : `${leadDays} day lead`;
  return status || "";
}

export function formatProductDeliverySummaryCompact(option = {}) {
  const status = PRODUCT_DELIVERY_STATUS_LABELS[safeStr(option.deliveryStatus)];
  const leadDays = normalizeStoredNumber(option.estimatedLeadTimeDays);
  const requiredOnSite = formatCompactProductDisplayDate(option.requiredOnSiteAt);
  const expectedDelivery = formatCompactProductDisplayDate(option.expectedDeliveryAt);
  const orderBy = formatCompactProductDisplayDate(option.orderByAt);
  const targetDate = requiredOnSite || expectedDelivery || orderBy;

  if (targetDate) {
    return status ? `${status} · ${targetDate}` : targetDate;
  }
  if (leadDays != null) return status ? `${status} · ${leadDays} day lead` : `${leadDays} day lead`;
  return status || "";
}

export function formatProductScheduleRiskLabel(option = {}) {
  return PRODUCT_SCHEDULE_RISK_LABELS[safeStr(option.scheduleRisk)] || "";
}

export function buildClientCommercialPublicationSnapshot({
  option = {},
  supplierDisplayName = "",
  supplierOrganizationId = "",
  decisionStatusLabel = "",
  publishDelivery = false,
} = {}) {
  const orgId = safeStr(supplierOrganizationId || option?.supplierOrganizationId || option?.organizationId);
  if (!publishDelivery) {
    const supplier = safeStr(supplierDisplayName);
    const decision = safeStr(decisionStatusLabel);
    const snapshot = {};
    if (supplier) snapshot.publishedSupplierName = supplier;
    if (orgId) snapshot.publishedSupplierOrganizationId = orgId;
    if (decision) snapshot.publishedDecisionStatusLabel = decision;
    return snapshot;
  }
  const snapshot = {};
  const supplier = safeStr(supplierDisplayName);
  const decision = safeStr(decisionStatusLabel);
  if (supplier) snapshot.publishedSupplierName = supplier;
  if (orgId) snapshot.publishedSupplierOrganizationId = orgId;
  if (decision) snapshot.publishedDecisionStatusLabel = decision;
  if (safeStr(option.expectedDeliveryAt)) snapshot.publishedExpectedDeliveryAt = safeStr(option.expectedDeliveryAt);
  const leadDays = normalizeStoredNumber(option.estimatedLeadTimeDays);
  if (leadDays != null) snapshot.publishedLeadTimeLabel = `${leadDays} day lead`;
  const deliveryLabel = PRODUCT_DELIVERY_STATUS_LABELS[safeStr(option.deliveryStatus)];
  if (deliveryLabel) snapshot.publishedDeliveryStatusLabel = deliveryLabel;
  const riskLabel = formatProductScheduleRiskLabel(option);
  if (riskLabel && safeStr(option.scheduleRisk) !== PRODUCT_SCHEDULE_RISK.NONE) {
    snapshot.publishedScheduleRiskLabel = riskLabel;
  }
  return snapshot;
}

export function clientCommercialSnapshotContainsForbiddenFields(snapshot = {}) {
  const serialized = JSON.stringify(snapshot);
  const forbidden = [
    "unitCost",
    "supplierQuote",
    "tax",
    "taxRate",
    "internalNotes",
    "deliveryEstimateSource",
    "estimatedProductTotal",
    "linkedBudgetItemId",
  ];
  return forbidden.some((token) => serialized.includes(`"${token}"`));
}
